import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import ImageUploader from '@/admin/ImageUploader';
import { TextField, Toggle, Section, Row, SaveBar } from '@/admin/AdminUI';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';

export default function FooterSettingsTab() {
  const [footer, setFooter] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get('/admin/settings').then((r) => setFooter(r.data?.footer || {}));
  }, []);

  if (!footer) return <div className="font-body text-ink-muted">Loading…</div>;

  const set = (k, v) => setFooter({ ...footer, [k]: v });
  const setSocial = (k, v) => setFooter({ ...footer, socials: { ...(footer.socials || {}), [k]: v } });
  const socials = footer.socials || {};
  const legal = Array.isArray(footer.legal_links) ? footer.legal_links : [];

  const setLegal = (i, k, v) => {
    const next = [...legal];
    next[i] = { ...next[i], [k]: v };
    set('legal_links', next);
  };
  const addLegal = () => set('legal_links', [...legal, { label: 'New Link', href: '#' }]);
  const removeLegal = (i) => set('legal_links', legal.filter((_, idx) => idx !== i));
  const moveLegal = (i, dir) => {
    const swap = i + dir;
    if (swap < 0 || swap >= legal.length) return;
    const next = [...legal];
    [next[i], next[swap]] = [next[swap], next[i]];
    set('legal_links', next);
  };

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await api.put('/admin/settings/footer', footer);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } finally { setSaving(false); }
  };

  return (
    <div>
      <Section title="Footer CTA Image">
        <ImageUploader value={footer.image_url} onChange={(v) => set('image_url', v)} label="Background Image" />
      </Section>

      <Section title="Footer Headline">
        <TextField label="Headline Line 1" value={footer.headline_line1} onChange={(v) => set('headline_line1', v)} />
        <TextField label="Headline Line 2" value={footer.headline_line2} onChange={(v) => set('headline_line2', v)} />
        <Row>
          <Toggle label="Line 1 in Orange" value={footer.headline_line1_orange} onChange={(v) => set('headline_line1_orange', v)} />
          <Toggle label="Line 2 in Orange" value={footer.headline_line2_orange} onChange={(v) => set('headline_line2_orange', v)} />
        </Row>
        <Row>
          <TextField label="Register CTA Label" value={footer.register_label} onChange={(v) => set('register_label', v)} />
          <TextField label="Register CTA URL" value={footer.register_url} onChange={(v) => set('register_url', v)} />
        </Row>
      </Section>

      <Section title="Footer Meta Bar">
        <Row>
          <TextField label="Logo Main Text" value={footer.meta_logo_main} onChange={(v) => set('meta_logo_main', v)} />
          <TextField label="Tagline" value={footer.meta_tagline} onChange={(v) => set('meta_tagline', v)} />
        </Row>
        <TextField label="Event Line (e.g. July 7–9, 2026 • Bellagio • Las Vegas)" value={footer.meta_event_line} onChange={(v) => set('meta_event_line', v)} />
        <TextField label="Copyright Text" value={footer.meta_copyright} onChange={(v) => set('meta_copyright', v)} />
      </Section>

      <Section title="Legal Links" action={<button onClick={addLegal} className="btn-primary text-[12px] !py-2 !px-4" data-testid="add-legal-link"><Plus size={14} className="mr-1" />Add Link</button>}>
        <div className="space-y-2">
          {legal.map((l, i) => (
            <div key={i} className="flex items-center gap-2 p-2 border border-border-line rounded-sm bg-white" data-testid="legal-link-row">
              <TextField label="" value={l.label} onChange={(v) => setLegal(i, 'label', v)} placeholder="Label" />
              <TextField label="" value={l.href} onChange={(v) => setLegal(i, 'href', v)} placeholder="URL" />
              <div className="flex flex-col gap-1">
                <button onClick={() => moveLegal(i, -1)} disabled={i === 0} className="p-1 disabled:opacity-30"><ChevronUp size={14} /></button>
                <button onClick={() => moveLegal(i, 1)} disabled={i === legal.length - 1} className="p-1 disabled:opacity-30"><ChevronDown size={14} /></button>
              </div>
              <button onClick={() => removeLegal(i)} className="btn-secondary !py-2 !px-3 text-[11px]"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Social URLs">
        <Row>
          <TextField label="LinkedIn" value={socials.linkedin} onChange={(v) => setSocial('linkedin', v)} />
          <TextField label="X (Twitter)" value={socials.twitter} onChange={(v) => setSocial('twitter', v)} />
        </Row>
        <Row>
          <TextField label="Instagram" value={socials.instagram} onChange={(v) => setSocial('instagram', v)} />
          <TextField label="YouTube" value={socials.youtube} onChange={(v) => setSocial('youtube', v)} />
        </Row>
      </Section>

      <SaveBar onSave={save} saving={saving} saved={saved} />
    </div>
  );
}
